/* eslint-disable @typescript-eslint/no-unused-vars */
import ControlledTable from "@/components/controlled-table";
import { useTableServer } from "@/hooks/useTableServer";
import { useCallback, useMemo, useState } from "react";
import EnquiryEntriesRepository from "@/utilities/repositories/EnquiryEntries";
import { getFeedbackColumns } from "./columns";
import { Title } from "rizzui";
import toast from "react-hot-toast";
import { LeadFeedbackStatus } from "@/utilities/types/EnquiryEntry";
import useProfile from "@/hooks/use-profile";
import { Roles } from "@/utilities/types/Users";

export default function LeadsFeedbackTable() {
  const { profile } = useProfile();
  const {
    isLoading,
    tableData,
    currentPage,
    totalItems,
    handlePaginate,
    pageSize,
    handleChangePageSize,
    searchTerm,
    handleSearch,
    sortConfig,
    handleSort,
    handleReset,
    refresh,
  } = useTableServer({
    server: {
      request:
        profile?.role === Roles.AGENCY
          ? EnquiryEntriesRepository.getListFeedbackByAgency
          : EnquiryEntriesRepository.getListFeedbackByUser,
    },
    searchBy: (searchTerm) => {
      const s = searchTerm || undefined;
      return {
        search: s,
      };
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onHeaderCellClick = useCallback(
    (value: string) => ({
      onClick: () => {
        handleSort(value);
      },
    }),
    [handleSort]
  );

  const handleFeedback = useCallback(
    async (id: string, type: LeadFeedbackStatus) => {
      try {
        setIsSubmitting(true);
        await EnquiryEntriesRepository.updateFeedback(id, type);
        await refresh();
        toast.success("Feedback Submitted");
      } catch (error) {
        toast.error("Failed to submit feedback");
      } finally {
        setIsSubmitting(false);
      }
    },
    [refresh]
  );

  const columns = useMemo(
    () =>
      getFeedbackColumns({
        data: tableData,
        sortConfig,
        onHeaderCellClick,
        onFeedback: handleFeedback,
        isSubmitting,
      }),
    [tableData, sortConfig, onHeaderCellClick, handleFeedback]
  );

  return (
    <div className="mb-4">
      <div className="flex items-center mb-5 gap-4">
        <div className="flex flex-col justify-start items-start gap-1">
          <Title as="h5" className="@xl:basis-auto text-xl md:text-2xl">
            Leads Feedback
          </Title>
          <p className="text-gray-500">
            To better optimise your ads, kindly share if you were able to
            contact the lead.
          </p>
        </div>
      </div>

      <ControlledTable
        variant="modern"
        data={tableData}
        isLoading={isLoading}
        showLoadingText={true}
        // @ts-expect-error - columns type mismatch
        columns={columns}
        paginatorOptions={{
          pageSize,
          setPageSize: handleChangePageSize,
          total: totalItems,
          current: currentPage,
          onChange: (page: number) => handlePaginate(page),
        }}
        className="rounded-md border border-muted text-sm shadow-sm [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:h-60 [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:justify-center [&_.rc-table-row:last-child_td.rc-table-cell]:border-b-0 [&_thead.rc-table-thead]:border-t-0"
      />
    </div>
  );
}
